/**
 * Custom Dialog Manager
 * Replaces browser alert() and confirm() with custom modal dialogs
 * that are easier to test with automated tools like Playwright
 */

export interface DialogOptions {
  title?: string;
  message: string;
  icon?: string;
  type?: "info" | "success" | "warning" | "error" | "confirm";
  confirmText?: string;
  cancelText?: string;
}

export class DialogManager {
  private dialogElement: HTMLElement | null = null;
  private titleElement: HTMLElement | null = null;
  private iconElement: HTMLElement | null = null;
  private messageElement: HTMLElement | null = null;
  private okButton: HTMLElement | null = null;
  private cancelButton: HTMLElement | null = null;

  constructor() {
    this.initializeElements();
  }

  private initializeElements(): void {
    // Wait for DOM to be ready if elements are not found
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        this.findElements();
      });
    } else {
      this.findElements();
    }
  }

  private findElements(): void {
    this.dialogElement = document.getElementById("notification-dialog");
    this.titleElement = document.getElementById("notification-title");
    this.iconElement = document.getElementById("notification-icon");
    this.messageElement = document.getElementById("notification-message");
    this.okButton = document.getElementById("notification-ok-btn");
    this.cancelButton = document.getElementById("notification-cancel-btn");

    if (!this.dialogElement) {
      console.warn(
        "Custom dialog elements not found in DOM. Using fallback alert/confirm."
      );
    }
  }

  /**
   * Show an informational dialog (replaces alert())
   */
  async showAlert(
    message: string,
    options: Partial<DialogOptions> = {}
  ): Promise<void> {
    await this.showDialog({
      type: "info",
      message,
      ...options,
    });
  }

  /**
   * Show a confirmation dialog (replaces confirm())
   */
  async showConfirm(
    message: string,
    options: Partial<DialogOptions> = {}
  ): Promise<boolean> {
    return this.showDialog({
      type: "confirm",
      message,
      confirmText: "OK",
      cancelText: "Cancel",
      ...options,
    }) as Promise<boolean>;
  }

  /**
   * Show a success notification
   */
  async showSuccess(
    message: string,
    options: Partial<DialogOptions> = {}
  ): Promise<void> {
    await this.showDialog({
      type: "success",
      icon: "✅",
      title: "Success",
      message,
      ...options,
    });
  }

  /**
   * Show an error notification
   */
  async showError(
    message: string,
    options: Partial<DialogOptions> = {}
  ): Promise<void> {
    await this.showDialog({
      type: "error",
      icon: "❌",
      title: "Error",
      message,
      ...options,
    });
  }

  /**
   * Show a warning notification
   */
  async showWarning(
    message: string,
    options: Partial<DialogOptions> = {}
  ): Promise<void> {
    await this.showDialog({
      type: "warning",
      icon: "⚠️",
      title: "Warning",
      message,
      ...options,
    });
  }

  /**
   * Show a custom dialog
   */
  private async showDialog(options: DialogOptions): Promise<void | boolean> {
    // Try to re-initialize elements if not found
    if (!this.dialogElement) {
      this.findElements();
    }

    // Fallback to native dialogs if custom elements are not available
    if (!this.dialogElement) {
      // Create a dynamic dialog if elements don't exist
      return this.showDynamicDialog(options);
    }

    // Set default values
    const {
      title = "Notification",
      message,
      icon = this.getDefaultIcon(options.type || "info"),
      type = "info",
      confirmText = "OK",
      cancelText = "Cancel",
    } = options;

    // Update dialog content
    if (this.titleElement) this.titleElement.textContent = title;
    if (this.iconElement) this.iconElement.textContent = icon;
    if (this.messageElement) this.messageElement.textContent = message;
    if (this.okButton) this.okButton.textContent = confirmText;
    if (this.cancelButton) this.cancelButton.textContent = cancelText;

    // Show/hide cancel button based on dialog type
    if (this.cancelButton) {
      this.cancelButton.style.display =
        type === "confirm" ? "inline-block" : "none";
    }

    // Update dialog styling based on type
    this.updateDialogStyling(type);

    // Show the dialog
    this.dialogElement.style.display = "flex";

    // Return a promise that resolves when user interacts with dialog
    return new Promise((resolve) => {
      const handleOk = () => {
        this.hideDialog();
        cleanup();
        resolve(type === "confirm" ? true : undefined);
      };

      const handleCancel = () => {
        this.hideDialog();
        cleanup();
        resolve(false);
      };

      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          this.hideDialog();
          cleanup();
          resolve(type === "confirm" ? false : undefined);
        }
      };

      const cleanup = () => {
        this.okButton?.removeEventListener("click", handleOk);
        this.cancelButton?.removeEventListener("click", handleCancel);
        document.removeEventListener("keydown", handleEscape);
      };

      // Add event listeners
      this.okButton?.addEventListener("click", handleOk);
      this.cancelButton?.addEventListener("click", handleCancel);
      document.addEventListener("keydown", handleEscape);

      // Focus the OK button for keyboard accessibility
      (this.okButton as HTMLButtonElement)?.focus();
    });
  }

  /**
   * Create and show a dynamic dialog when HTML elements are not available
   */
  private async showDynamicDialog(
    options: DialogOptions
  ): Promise<void | boolean> {
    const {
      title = "Notification",
      message,
      icon = this.getDefaultIcon(options.type || "info"),
      type = "info",
      confirmText = "OK",
      cancelText = "Cancel",
    } = options;

    // Create dialog structure
    const dialogElement = document.createElement("div");
    dialogElement.id = "dynamic-dialog";
    dialogElement.className = "notification-dialog";
    dialogElement.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10000;
    `;

    const contentElement = document.createElement("div");
    contentElement.className = "notification-content";
    contentElement.style.cssText = `
      background: white;
      border-radius: 12px;
      min-width: 320px;
      max-width: 500px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    `;

    const headerElement = document.createElement("div");
    headerElement.style.cssText = `
      display: flex;
      align-items: center;
      padding: 20px 20px 15px 20px;
      border-bottom: 1px solid #e9ecef;
    `;

    const iconElement = document.createElement("div");
    iconElement.textContent = icon;
    iconElement.style.cssText = `
      font-size: 24px;
      margin-right: 12px;
    `;

    const titleElement = document.createElement("h3");
    titleElement.textContent = title;
    titleElement.style.cssText = `
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #333;
    `;

    const bodyElement = document.createElement("div");
    bodyElement.style.cssText = `
      padding: 20px;
      color: #555;
      line-height: 1.5;
    `;

    const messageElement = document.createElement("p");
    messageElement.textContent = message;
    messageElement.style.cssText = "margin: 0;";

    const buttonsElement = document.createElement("div");
    buttonsElement.style.cssText = `
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      padding: 15px 20px 20px 20px;
      border-top: 1px solid #e9ecef;
    `;

    const okButton = document.createElement("button");
    okButton.textContent = confirmText;
    okButton.setAttribute("data-testid", "dynamic-dialog-ok");
    okButton.style.cssText = `
      padding: 8px 16px;
      border: none;
      border-radius: 6px;
      font-weight: 500;
      cursor: pointer;
      background: #667eea;
      color: white;
    `;

    const cancelButton = document.createElement("button");
    cancelButton.textContent = cancelText;
    cancelButton.setAttribute("data-testid", "dynamic-dialog-cancel");
    cancelButton.style.cssText = `
      padding: 8px 16px;
      border: 1px solid #dee2e6;
      border-radius: 6px;
      font-weight: 500;
      cursor: pointer;
      background: #f8f9fa;
      color: #6c757d;
      ${type === "confirm" ? "" : "display: none;"}
    `;

    // Assemble the dialog
    headerElement.appendChild(iconElement);
    headerElement.appendChild(titleElement);
    bodyElement.appendChild(messageElement);
    buttonsElement.appendChild(okButton);
    if (type === "confirm") {
      buttonsElement.appendChild(cancelButton);
    }

    contentElement.appendChild(headerElement);
    contentElement.appendChild(bodyElement);
    contentElement.appendChild(buttonsElement);
    dialogElement.appendChild(contentElement);

    // Add to DOM
    document.body.appendChild(dialogElement);

    // Return promise that resolves when user interacts
    return new Promise((resolve) => {
      const cleanup = () => {
        document.body.removeChild(dialogElement);
      };

      const handleOk = () => {
        cleanup();
        resolve(type === "confirm" ? true : undefined);
      };

      const handleCancel = () => {
        cleanup();
        resolve(false);
      };

      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          cleanup();
          document.removeEventListener("keydown", handleEscape);
          resolve(type === "confirm" ? false : undefined);
        }
      };

      okButton.addEventListener("click", handleOk);
      cancelButton.addEventListener("click", handleCancel);
      document.addEventListener("keydown", handleEscape);

      // Focus the OK button
      okButton.focus();
    });
  }

  private getDefaultIcon(type: string): string {
    switch (type) {
      case "success":
        return "✅";
      case "error":
        return "❌";
      case "warning":
        return "⚠️";
      case "confirm":
        return "❓";
      default:
        return "🔔";
    }
  }

  private updateDialogStyling(type: string): void {
    if (!this.dialogElement) return;

    // Remove existing type classes
    this.dialogElement.classList.remove(
      "dialog-info",
      "dialog-success",
      "dialog-error",
      "dialog-warning",
      "dialog-confirm"
    );

    // Add new type class
    this.dialogElement.classList.add(`dialog-${type}`);
  }

  private hideDialog(): void {
    if (this.dialogElement) {
      this.dialogElement.style.display = "none";
    }
  }
}

// Create global instance
export const dialogManager = new DialogManager();

// Override global alert and confirm for backward compatibility
(window as any).customAlert = (message: string) =>
  dialogManager.showAlert(message);
(window as any).customConfirm = (message: string) =>
  dialogManager.showConfirm(message);
